import { cn } from 'noddi-ui-common';
import { VisuallyHidden as VisuallyHiddenPrimitive } from 'radix-ui';
import { PropsWithChildren, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../base/dialog';
import { OverlayActionsSlot } from './OverlayActionsSlot';
import { onOverlayOpenAutoFocus } from './onOverlayOpenAutoFocus';

export type NoddiDialogProps = PropsWithChildren<{
  open: boolean;
  onClose: (event?: unknown, reason?: string) => void;
  title?: string;
  maxWidth?: MaxWidth;
  className?: string;
  description?: string;
  dialogHeaderClassName?: string;
  /**
   * Classes for the body wrapper. Pass `flex min-h-0 flex-1 flex-col overflow-hidden` together with a
   * fixed `className` height to hand the scroll region to a fillHeight child instead of the wrapper.
   */
  contentClassName?: string;
  /**
   * Actions (Cancel / Save) and anything else that must stay in view, pinned under the body. The
   * body scrolls once its content outgrows the viewport, so an action row at its end scrolls out of
   * sight (`noddi/overlay-actions-in-footer`). A body component that owns its actions renders them
   * through `NavioOverlayActions`, which lands here too.
   */
  footer?: React.ReactNode;
  showCloseButton?: boolean;
}>;

const NoddiDialog = ({
  children,
  onClose,
  open = false,
  title,
  maxWidth = 'sm',
  className,
  description,
  dialogHeaderClassName,
  contentClassName,
  footer,
  showCloseButton
}: NoddiDialogProps) => {
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
      <DialogContent
        onOpenAutoFocus={onOverlayOpenAutoFocus}
        className={cn(getMaxWidthClass(maxWidth), 'flex flex-col', className)}
        showCloseButton={showCloseButton}
      >
        <DialogHeader className={dialogHeaderClassName}>
          <DialogTitle>{title || <VisuallyHiddenPrimitive.Root>Dialog</VisuallyHiddenPrimitive.Root>} </DialogTitle>
          <DialogDescription>
            {description || <VisuallyHiddenPrimitive.Root>No description</VisuallyHiddenPrimitive.Root>}
          </DialogDescription>
        </DialogHeader>
        {/* `-m-1 p-1` keeps the visual padding unchanged while giving focus rings on edge
            elements 4px of room -- a scroll container clips them at its padding box.
            `empty:hidden`: a dialog whose only content is its footer gets no extra gap. */}
        <div className={cn('minimal-scrollbar-hover -m-1 min-h-0 overflow-auto p-1 empty:hidden', contentClassName)}>
          <OverlayActionsSlot value={actionsSlot}>{children}</OverlayActionsSlot>
        </div>
        <div ref={setActionsSlot} className='flex shrink-0 flex-col gap-4 empty:hidden'>
          {footer}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { NoddiDialog };

type MaxWidth = 'sm' | 'md' | 'lg' | 'xl';

const getMaxWidthClass = (maxWidth: MaxWidth) => {
  const widthMap = {
    sm: 'sm:max-w-xl',
    md: 'sm:max-w-4xl',
    lg: 'sm:max-w-5xl',
    xl: 'sm:max-w-7xl'
  };
  return widthMap[maxWidth];
};
